import { ChangeDetectionStrategy, Component, inject, Input, ViewEncapsulation } from '@angular/core';
import { ILightWeightBusinessUnit } from '@fi-shared/models';
import { isDefined } from '@fi-shared/utils';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { ObjectTypes } from '@fi-shared/types';
import { FiAccessRightHelperService } from '@fi-shared/fi-shared-services/fi-access-right-helper.service';
import { IFiDropdownEventData } from '@fi-shared/fi-select-search/fi-select-search';

@Component({
    selector: 'fi-individual-units-section',
    template: `
        <div class="individual-units-section">
            @if (isUnitsSectionHeaderVisible) {
                @if (headingText) {
                    <div class="individual-units-section-header-wrapper">
                        <p class="sharing-selection-section-heading sub-heading">{{ headingText }}</p>
                    </div>
                }

                @if (descriptionText) {
                    <p class="sharing-selection-section-description">{{ descriptionText }}</p>
                }
            }

            <fi-chips-select-search
                [(ngModel)]="ngModel"
                (ngModelChange)="doPropagateChanges()"
                [ngModelOptions]="{ standalone: true }"
                [isOptionDisabledFn]="isOptionDisabled.bind(this)"
                [canRemoveItemFn]="canRemoveItem.bind(this)"
                [getOptionTextClass]="getOptionTextClass.bind(this)"
                [items]="businessUnits"
                [label]="'common.units' | fiAppText"
                appearance="outline"
                autoCompletePanelOpenedIconName="fas fa-caret-up"
                autoCompletePanelClosedIconName="fas fa-caret-down"
                displayValue="Name"
                panelClass="fi-rounded-chips-panel fi-chips-panel-default-padding-left"
                [isFullWidthOptions]="true"
            />
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-individual-units-section' },
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiIndividualUnitsSectionComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiIndividualUnitsSectionComponent,
            multi: true,
        },
    ],
})
export class FiIndividualUnitsSectionComponent extends ControlValueAccessorValidatorBase<number[]> {
    @Input() businessUnits: ILightWeightBusinessUnit[] = [];
    @Input() isItemCreator: boolean;
    @Input() businessUnitIdsOutOfScope: number[] = [];
    @Input() headingText: string;
    @Input() descriptionText: string;
    @Input() isUnitsSectionHeaderVisible: boolean;
    private fiAccessRightHelperService: FiAccessRightHelperService = inject(FiAccessRightHelperService);

    private isUnitOutOfScope(businessUnitId: number): boolean {
        return this.businessUnitIdsOutOfScope.indexOf(businessUnitId) !== -1;
    }

    private isSystemUnit(unit: ILightWeightBusinessUnit): boolean {
        return unit.IsSystemObject;
    }

    protected isOptionDisabled(unit: ILightWeightBusinessUnit): boolean {
        if (!isDefined(unit)) return true;

        return !unit.IsActivated || (!this.isItemCreator && this.isUnitOutOfScope(unit.Id));
    }

    protected canRemoveItem($event: IFiDropdownEventData<ILightWeightBusinessUnit>): Promise<{ result: boolean }> {
        return new Promise<{ result: boolean }>((resolve, reject) => {
            if (!this.isUnitOutOfScope($event.item.Id)) {
                resolve({ result: true });
                return;
            }

            this.fiAccessRightHelperService
                .deletingUnavailableAccessRightItemConfirm(ObjectTypes.BusinessUnit, undefined)
                .then((canDelete: boolean) => resolve({ result: canDelete }));
        });
    }

    protected getOptionTextClass(unit: ILightWeightBusinessUnit): string {
        return this.isUnitOutOfScope(unit.Id) || this.isSystemUnit(unit) ? 'color-red' : '';
    }
}
